/**
 * Minimal client-side CSV export helper.
 * (No CSV utility existed elsewhere in the codebase to follow as a convention.)
 */
export function exportToCsv(filename: string, rows: Array<Record<string, unknown>>): void {
  if (rows.length === 0) return;

  const headers = Object.keys(rows[0]);
  const escape = (value: unknown): string => {
    const str = value === null || value === undefined ? "" : String(value);
    if (str.includes(",") || str.includes('"') || str.includes("\n")) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const lines = [
    headers.join(","),
    ...rows.map((row) => headers.map((header) => escape(row[header])).join(",")),
  ];

  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Minimal client-side "Excel" export — no `xlsx`/SheetJS dependency added
 * for this. Builds an HTML `<table>` wrapped in the Excel-specific
 * `urn:schemas-microsoft-com:office:excel` namespace, which Excel opens as
 * a proper worksheet (each `<td>` becomes a cell, not one big text blob)
 * when saved with a `.xls` extension — a long-standing, well-supported
 * technique for dependency-free Excel export. This is NOT a real `.xlsx`
 * (OOXML) binary file; if a genuinely native `.xlsx` is ever required,
 * that would need the `xlsx` (SheetJS) package added as a real dependency.
 */
export function exportToExcel(filename: string, rows: Array<Record<string, unknown>>): void {
  if (rows.length === 0) return;

  const headers = Object.keys(rows[0]);
  const escapeHtml = (value: unknown): string => {
    const str = value === null || value === undefined ? "" : String(value);
    return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  };

  const headerRow = `<tr>${headers.map((h) => `<th>${escapeHtml(h)}</th>`).join("")}</tr>`;
  const bodyRows = rows
    .map((row) => `<tr>${headers.map((h) => `<td>${escapeHtml(row[h])}</td>`).join("")}</tr>`)
    .join("");

  const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Sheet1</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head>
      <body><table>${headerRow}${bodyRows}</table></body>
    </html>`;

  const blob = new Blob([html], { type: "application/vnd.ms-excel" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename.endsWith(".xls") ? filename : `${filename}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
